import { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import api, { BACKEND_URL, getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { extractError } from "../lib/formatError";

const POLL_MS = 5000;
const WS_RETRY_MS = 4000;
const MAX_LEN = 2000;

export default function ChatWidget({ hidden = false }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const bottomRef = useRef(null);
  const pollTimerRef = useRef(null);

  const unread = conv?.unread_user_count || 0;

  const load = async () => {
    try {
      const { data } = await api.get("/chat/my-conversation");
      setConv(data.conversation);
      setMessages(data.messages);
      setError(null);
    } catch (e) { setError(extractError(e)); }
  };

  // Load on open (or immediately if we want an unread badge). Poll every 5s while active.
  useEffect(() => {
    if (!user || hidden) return;
    load();
    pollTimerRef.current = setInterval(load, POLL_MS);
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
    // eslint-disable-next-line
  }, [user?.id, hidden]);

  // WebSocket (upgrade over polling)
  useEffect(() => {
    if (!user || hidden) return;
    let cancelled = false;
    let retry;

    const connect = () => {
      if (cancelled) return;
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const host = BACKEND_URL.replace(/^https?:\/\//, "");
      const url = `${proto}://${host}/ws/chat?token=${encodeURIComponent(getToken() || "")}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => setWsConnected(true);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "message" && data.message) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
            if (data.conversation) setConv(data.conversation);
          }
        } catch (e) { /* ignore */ }
      };
      ws.onerror = () => { /* onclose will handle it */ };
      ws.onclose = () => {
        setWsConnected(false);
        wsRef.current = null;
        if (!cancelled) retry = setTimeout(connect, WS_RETRY_MS);
      };
    };
    connect();
    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* ignore */ } }
    };
    // eslint-disable-next-line
  }, [user?.id, hidden]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, open]);

  // Expose an imperative opener so the top-nav "Live chat" button, the mobile
  // menu entry, and page-level entry points (e.g. WalletDeposit support CTA)
  // can open the drawer without needing the floating FAB in the DOM. The FAB
  // was removed in T7-follow-up as duplicative with the top-nav entry.
  useEffect(() => {
    window.__openBraGameChat = () => setOpen(true);
    return () => {
      try { delete window.__openBraGameChat; } catch { window.__openBraGameChat = undefined; }
    };
  }, []);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/chat/messages", { body });
      setMessages((prev) => [...prev, data.message]);
      setText("");
    } catch (e2) { setError(extractError(e2)); } finally { setBusy(false); }
  };

  if (!user || hidden) return null;
  if (!open) return null;

  return (
    <div className="fixed bottom-6 right-6 z-40" data-testid="chat-widget">
      <div className="w-[360px] max-w-[calc(100vw-3rem)] h-[520px] max-h-[80vh] flex flex-col border shadow-2xl" style={{ background: "var(--card)", borderColor: "var(--line)" }}>
        <header className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--line)" }}>
          <div className="flex items-center gap-2">
            <div className={`h-2 w-2 rounded-full ${wsConnected ? "bg-emerald-400" : "bg-yellow-500"}`} title={wsConnected ? "Live" : "Polling"} />
            <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)]">Support</div>
          </div>
          <button onClick={() => setOpen(false)} className="text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-testid="chat-close-btn">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="text-xs text-[color:var(--muted)] text-center pt-8 italic">
              Ask us anything — we typically reply within a few minutes.
            </div>
          ) : (
            messages.map((m) => <MsgBubble key={m.id} m={m} />)
          )}
          <div ref={bottomRef} />
        </div>

        {error && <div className="text-[10px] text-[color:var(--red)] px-3 py-1">{error}</div>}

        <form onSubmit={send} className="flex items-end gap-2 p-3 border-t" style={{ borderColor: "var(--line)" }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
            placeholder="Type a message…"
            rows={2}
            className="flex-1 bet-input text-xs resize-none"
            data-testid="chat-input"
          />
          <button type="submit" disabled={!text.trim() || busy} className="p-2 border border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[color:var(--card)] disabled:opacity-40" data-testid="chat-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
}

function MsgBubble({ m }) {
  const isUser = m.sender_type === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className="max-w-[80%] px-3 py-2 text-xs whitespace-pre-wrap break-words border"
        style={{
          background: isUser ? "rgba(212,161,58,0.1)" : "var(--shell-2)",
          borderColor: isUser ? "var(--gold)" : "var(--line)",
          color: "var(--ink)",
        }}
        data-testid={`chat-msg-${m.id}`}
      >
        <div>{m.body}</div>
        <div className="text-[9px] tracking-widest uppercase text-[color:var(--muted)] mt-1">
          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          {m.sender_type === "admin" && " · Support"}
        </div>
      </div>
    </div>
  );
}
